import React, { useCallback, useEffect, useState } from "react";
import Api from "../utils/Api";
import SwalHelper from "../utils/Swal";
import LoadingOverlay from "../components/LoadingOverlay";
import {
  MdAdd,
  MdRefresh,
  MdSearch,
  MdVisibility,
  MdEdit,
  MdDelete,
} from "react-icons/md";
import ModalDetailQuotation from "../components/modals/quotation/ModalDetailQuotation";
import ModalCreateQuotation from "../components/modals/quotation/ModalCreateQuotation";
import ModalEditQuotation from "../components/modals/quotation/ModalEditQuotation";
import DocumentPreviewModal from "../components/modals/DocumentPreviewModal";
import { STAGE_BADGES, STAGE_LABELS } from "../utils/WorkBadge";
import { formatFullDate, formatTanggalIndoLengkap } from "../utils/Helpers";

const STATUS_OPTIONS = [
  { value: "", label: "Semua Status" },
  { value: "DRAFT", label: "Draft" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "WON", label: "Won" },
  { value: "LOST", label: "Lost" },
  { value: "EXPIRED", label: "Expired" },
  { value: "CANCELLED", label: "Cancelled" },
];

const STAGE_FILTER_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "CLOSED", label: "Closed" },
];

const PER_PAGE_OPTIONS = [25, 50, 100];

const Quotation = () => {
  const [dataQuotation, setDataQuotation] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [selectedQuotationId, setSelectedQuotationId] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [selectedDocumentUrl, setSelectedDocumentUrl] = useState("");
  const [selectedDocumentTitle, setSelectedDocumentTitle] = useState("");

  const [filter, setFilter] = useState({
    stage: "ACTIVE",
    search: "",
    status: "",
    id_client: "",
    id_work_item: "",
  });

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 25,
    total: 0,
    total_pages: 1,
  });

  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });

  const fetchQuotations = useCallback(async () => {
    setLoading(true);

    try {
      const params = {
        stage: filter.stage,
      };

      if (filter.search.trim()) {
        params.search = filter.search.trim();
      }

      if (filter.status) {
        params.status = filter.status;
      }

      if (filter.id_client) {
        params.id_client = Number(filter.id_client);
      }

      if (filter.id_work_item) {
        params.id_work_item = Number(filter.id_work_item);
      }

      if (filter.stage === "CLOSED") {
        params.page = page;
        params.per_page = perPage;
      }

      const response = await Api.get("/work-item/quotation", { params });

      if (response.data?.success) {
        const responseData = response.data.data || {};
        const items = responseData.items || [];

        setDataQuotation(items);

        if (filter.stage === "CLOSED") {
          const pageInfo = responseData.pagination || {};

          setPagination({
            page: pageInfo.page || page,
            per_page: pageInfo.per_page || perPage,
            total: pageInfo.total || 0,
            total_pages: pageInfo.total_pages || 1,
          });
        } else {
          setPagination({
            page: 1,
            per_page: items.length,
            total: items.length,
            total_pages: 1,
          });
        }
      }
    } catch (error) {
      console.error("Gagal mengambil data quotation:", error);

      SwalHelper.error(
        error.response?.data?.message || "Gagal mengambil data penawaran.",
      );
    } finally {
      setLoading(false);
    }
  }, [filter, page, perPage]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await Api.get("/work-item/master/clients/options");

      if (response.data?.success) {
        setClients(response.data.data || []);
      }
    } catch (error) {
      console.error("Gagal mengambil data client:", error);

      SwalHelper.error(
        error.response?.data?.message || "Gagal mengambil data client.",
      );
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    fetchQuotations();
  }, [fetchQuotations]);

  const handleFilterChange = (key, value) => {
    setPage(1);

    setFilter((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleResetFilter = () => {
    setPage(1);

    setFilter((current) => ({
      stage: current.stage,
      search: "",
      status: "",
      id_client: "",
      id_work_item: "",
    }));
  };

  const handleRefresh = () => {
    fetchQuotations();
  };

  const handlePerPageChange = (value) => {
    setPerPage(Number(value));
    setPage(1);
  };

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "desc" ? "asc" : "desc",
    }));
  };

  const displayedData = [...dataQuotation].sort((a, b) => {
    const { key, direction } = sortConfig;

    let valueA = a[key];
    let valueB = b[key];

    if (
      key === "created_at" ||
      key === "proposal_date" ||
      key === "valid_until"
    ) {
      valueA = new Date(valueA || 0).getTime();
      valueB = new Date(valueB || 0).getTime();
    }

    if (key === "proposal_value") {
      valueA = Number(valueA || 0);
      valueB = Number(valueB || 0);
    }

    if (typeof valueA === "string") {
      valueA = valueA.toLowerCase();
      valueB = (valueB || "").toLowerCase();
    }

    if (valueA < valueB) return direction === "asc" ? -1 : 1;
    if (valueA > valueB) return direction === "asc" ? 1 : -1;

    return 0;
  });

  const handleCreate = () => {
    setShowCreate(true);
  };

  const handleDetail = (item) => {
    setSelectedQuotationId(item.id_proposal);
    setShowDetail(true);
  };

  const handleEdit = (item) => {
    setSelectedQuotationId(item.id_proposal);
    setShowEdit(true);
  };

  const handleDocumentPreview = (item) => {
    if (!item.document_url) {
      SwalHelper.warning("Dokumen belum tersedia.");
      return;
    }

    setSelectedDocumentUrl(item.document_url);
    setSelectedDocumentTitle(
      `${item.proposal_number} - ${item.work_name || "Penawaran"}`,
    );
    setShowDocumentPreview(true);
  };

  const handleDelete = async (item) => {
    const confirmed = await SwalHelper.confirm({
      title: "Nonaktifkan Penawaran?",
      message: `Penawaran "${item.proposal_number}" akan dinonaktifkan.`,
      confirmText: "Ya, Nonaktifkan",
      cancelText: "Batal",
    });

    if (!confirmed) return;

    try {
      setLoading(true);

      const response = await Api.delete(
        `/work-item/quotation/${item.id_proposal}`,
      );

      if (response.data?.success) {
        await SwalHelper.success(
          response.data?.message || "Penawaran berhasil dinonaktifkan.",
        );
        await fetchQuotations();
      } else {
        SwalHelper.error(
          response.data?.message || "Gagal menonaktifkan penawaran.",
        );
      }
    } catch (error) {
      SwalHelper.error(
        error.response?.data?.message || "Gagal menonaktifkan penawaran.",
      );
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";

    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getStatusClass = (status) => {
    const classes = {
      DRAFT: "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300",

      SUBMITTED:
        "bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400",

      WON: "bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-400",

      LOST: "bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400",

      EXPIRED:
        "bg-orange-50 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400",

      CANCELLED:
        "bg-slate-100 text-slate-600 dark:bg-white/10 dark:text-gray-300",
    };

    return (
      classes[status] ||
      "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300"
    );
  };

  const formatStatus = (status) => {
    const labels = {
      DRAFT: "Draft",
      SUBMITTED: "Submitted",
      WON: "Won",
      LOST: "Lost",
      EXPIRED: "Expired",
      CANCELLED: "Cancelled",
    };

    return labels[status] || status || "-";
  };

  return (
    <>
      {loading && <LoadingOverlay message="Memuat Penawaran..." />}

      <div className="space-y-3 pb-3 font-poppins">
        {/* =====================================================
            HEADER
        ====================================================== */}

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 px-1">
          <div>
            <h1 className="text-xl font-black text-custom-gelap dark:text-white uppercase tracking-tighter">
              Penawaran
            </h1>

            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[2px]">
              Quotation Management
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-custom-gelap border border-gray-100 dark:border-white/5 text-custom-gelap dark:text-white rounded-2xl text-[9px] font-black uppercase tracking-widest shadow-sm transition-all hover:scale-105 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <MdRefresh size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>

            <button
              type="button"
              onClick={handleCreate}
              className="flex items-center gap-2 px-5 py-2.5 bg-custom-merah-terang text-white rounded-2xl text-[9px] font-black uppercase tracking-widest shadow-lg transition-all hover:scale-105 active:scale-95"
            >
              <MdAdd size={16} />
              Penawaran Baru
            </button>
          </div>
        </div>

        {/* =====================================================
            FILTER
        ====================================================== */}

        <div className="bg-white dark:bg-custom-gelap border border-gray-100 dark:border-white/5 rounded-2xl p-4">
          <div className="flex items-center gap-2 overflow-x-auto">
            {/* Stage */}
            <div className="flex shrink-0 items-center gap-1 rounded-xl bg-gray-100 p-1 dark:bg-white/5">
              {STAGE_FILTER_OPTIONS.map((option) => {
                const active = filter.stage === option.value;

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleFilterChange("stage", option.value)}
                    className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-[9px] font-black uppercase tracking-widest transition-all ${
                      active
                        ? "bg-custom-merah-terang text-white shadow-sm"
                        : "text-gray-400 hover:bg-white hover:text-custom-gelap dark:hover:bg-white/10 dark:hover:text-white"
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            {/* Search */}
            <div className="relative min-w-[260px] flex-1">
              <MdSearch
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />

              <input
                type="text"
                value={filter.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                placeholder="Cari nomor penawaran..."
                className="h-10 w-full rounded-xl border border-gray-100 bg-gray-50 pl-9 pr-3 text-[10px] font-bold text-custom-gelap outline-none placeholder:text-gray-400 focus:border-custom-merah-terang dark:border-white/10 dark:bg-white/5 dark:text-white"
              />
            </div>

            <select
              value={filter.id_client}
              onChange={(e) => handleFilterChange("id_client", e.target.value)}
              className="h-10 w-44 shrink-0 cursor-pointer rounded-xl border border-gray-100 bg-gray-50 px-3 text-[10px] font-black text-custom-gelap outline-none focus:border-custom-merah-terang dark:border-white/10 dark:bg-white/5 dark:text-white"
            >
              <option value="">Semua Client</option>

              {clients.map((client) => (
                <option key={client.id_client} value={client.id_client}>
                  {client.name}
                </option>
              ))}
            </select>

            {/* Status */}
            <select
              value={filter.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="h-10 w-44 shrink-0 cursor-pointer rounded-xl border border-gray-100 bg-gray-50 px-3 text-[10px] font-black text-custom-gelap outline-none focus:border-custom-merah-terang dark:border-white/10 dark:bg-white/5 dark:text-white"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            {/* Reset */}
            <button
              type="button"
              onClick={handleResetFilter}
              className="flex h-10 shrink-0 items-center gap-2 rounded-xl bg-gray-100 px-4 text-[9px] font-black uppercase tracking-widest text-custom-gelap transition-all hover:bg-gray-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
            >
              <MdRefresh size={14} />
              Reset
            </button>
          </div>
        </div>

        {/* =====================================================
            TABLE
        ====================================================== */}

        <div className="bg-white dark:bg-custom-gelap border border-gray-100 dark:border-white/5 rounded-2xl overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-260px)]">
            <table className="w-full min-w-[1200px]">
              <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-custom-gelap">
                <tr className="bg-gray-50 dark:bg-white/5 border-b border-gray-100 dark:border-white/5">
                  <th className="w-12 px-4 py-3 text-center text-[10px] font-black text-gray-400 uppercase tracking-wider">
                    #
                  </th>
                  <th
                    className="px-4 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-wider cursor-pointer whitespace-nowrap"
                    onClick={() => handleSort("proposal_number")}
                  >
                    No. Penawaran
                  </th>
                  <th
                    className="px-4 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-wider cursor-pointer whitespace-nowrap"
                    onClick={() => handleSort("work_number")}
                  >
                    Nama Pekerjaan
                  </th>
                  <th
                    className="px-4 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-wider cursor-pointer whitespace-nowrap"
                    onClick={() => handleSort("client_name")}
                  >
                    PIC - Client
                  </th>
                  <th
                    className="px-4 py-3 text-right text-[10px] font-black text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer"
                    onClick={() => handleSort("proposal_value")}
                  >
                    Nilai Penawaran
                  </th>
                  <th
                    className="px-4 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer"
                    onClick={() => handleSort("proposal_date")}
                  >
                    Tanggal
                  </th>
                  <th
                    className="px-4 py-3 text-left text-[10px] font-black text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer"
                    onClick={() => handleSort("valid_until")}
                  >
                    Tanggal Berakhir
                  </th>
                  <th
                    className="px-4 py-3 text-center text-[10px] font-black text-gray-400 uppercase tracking-wider whitespace-nowrap cursor-pointer"
                    onClick={() => handleSort("status")}
                  >
                    Status
                  </th>
                  <th className="px-4 py-3 text-center text-[10px] font-black text-gray-400 uppercase tracking-wider">
                    Lampiran
                  </th>
                  <th className="px-4 py-3 text-center text-[10px] font-black text-gray-400 uppercase tracking-wider">
                    Aksi
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100 dark:divide-white/5">
                {displayedData.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-4 py-12 text-center">
                      <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest">
                        Tidak ada data penawaran
                      </p>
                    </td>
                  </tr>
                ) : (
                  displayedData.map((item, index) => (
                    <tr
                      key={item.id_proposal}
                      className="hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors"
                    >
                      {/* # */}
                      <td className="px-4 py-3 text-center text-[11px] font-bold text-gray-400">
                        {(pagination.page - 1) * pagination.per_page +
                          index +
                          1}
                      </td>

                      {/* QUOTATION NUMBER */}
                      <td className="px-4 py-3 align-top">
                        <button
                          type="button"
                          onClick={() => handleDetail(item)}
                          className="text-[11px] font-black text-custom-merah-terang hover:underline whitespace-nowrap"
                        >
                          {item.proposal_number}
                        </button>

                        <p className="text-[9px] text-gray-400 font-bold mt-0.5 whitespace-nowrap">
                          {formatTanggalIndoLengkap(item.created_at)}
                        </p>
                      </td>

                      {/* WORK-ITEM NAME */}
                      <td className="px-4 py-3 align-top min-w-[320px] max-w-[420px]">
                        {/* Work Number + Work Stage */}
                        <div className="flex items-center gap-2">
                          <span className="whitespace-nowrap text-[9px] font-bold text-gray-400">
                            {item.work_number || "-"}
                          </span>

                          {item.current_stage && (
                            <span
                              className={`inline-flex items-center rounded-md px-2 py-0.5 text-[8px] font-black uppercase tracking-wide ${
                                STAGE_BADGES[item.current_stage] ||
                                "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300"
                              }`}
                            >
                              {STAGE_LABELS[item.current_stage] ||
                                item.current_stage}
                            </span>
                          )}
                        </div>

                        {/* Work Name */}
                        <p
                          className="mt-1 line-clamp-2 text-[11px] font-black leading-4 text-custom-gelap dark:text-white"
                          title={item.work_name}
                        >
                          {item.work_name || "-"}
                        </p>
                      </td>

                      {/* PIC & CLIENT */}
                      <td className="px-4 py-3 align-top">
                        <p className="text-[11px] font-black text-custom-gelap dark:text-white whitespace-nowrap">
                          Pak {item.client_pic_name || "-"}
                        </p>
                        <p className="text-[9px] text-gray-400 font-bold mt-0.5 whitespace-nowrap">
                          {item.client_name || "-"}
                        </p>
                      </td>

                      {/* QUOTATION VALUE */}
                      <td className="px-4 py-3 align-top text-right whitespace-nowrap">
                        <p className="mt-0.5 text-[13px] font-black leading-4 whitespace-nowrap text-custom-gelap dark:text-white">
                          {formatCurrency(item.proposal_value)}
                        </p>
                      </td>

                      {/* QUOTATION DATE */}
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        {(() => {
                          const date = formatFullDate(item.proposal_date);

                          if (!date) {
                            return (
                              <span className="text-[10px] font-bold text-gray-400">
                                -
                              </span>
                            );
                          }

                          return (
                            <div className="flex flex-col">
                              <span className="text-[9px] font-black uppercase tracking-[1.5px] dark:text-white">
                                {date.dayName}
                              </span>

                              <span className="mt-0.5 text-[12px] font-black leading-4 whitespace-nowrap text-custom-gelap dark:text-white">
                                {date.date}
                              </span>
                            </div>
                          );
                        })()}
                      </td>

                      {/* DUE DATE */}
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        {(() => {
                          const date = formatFullDate(item.valid_until);

                          if (!date) {
                            return (
                              <span className="text-[10px] font-bold text-gray-400">
                                -
                              </span>
                            );
                          }

                          return (
                            <div className="flex flex-col">
                              <span className="text-[9px] font-black uppercase tracking-[1.5px]  dark:text-white">
                                {date.dayName}
                              </span>

                              <span className="mt-0.5 text-[12px] font-black leading-4 whitespace-nowrap text-custom-merah-terang">
                                {date.date}
                              </span>
                            </div>
                          );
                        })()}
                      </td>

                      {/* STATUS */}
                      <td className="px-4 py-3 text-center align-top">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider whitespace-nowrap ${getStatusClass(item.status)}`}
                        >
                          {formatStatus(item.status)}
                        </span>
                      </td>

                      {/* ATTACHMENT */}
                      <td className="px-4 py-3 align-top text-center">
                        {item.document_url ? (
                          <button
                            type="button"
                            onClick={() => handleDocumentPreview(item)}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-custom-merah-terang/20 bg-custom-merah-terang/10 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-custom-merah-terang transition-all hover:-translate-y-0.5 hover:bg-custom-merah-terang/15 dark:border-custom-merah-terang/30 dark:bg-custom-merah-terang/10 dark:text-custom-cerah dark:hover:bg-custom-merah-terang/20"
                            title="Preview Dokumen"
                          >
                            <MdVisibility size={14} />
                            Preview
                          </button>
                        ) : (
                          <span className="inline-flex items-center rounded-xl bg-gray-100 px-3 py-2 text-[9px] font-black uppercase whitespace-nowrap tracking-widest text-gray-400 dark:bg-white/5 dark:text-gray-500">
                            No File
                          </span>
                        )}
                      </td>

                      {/* ACTION */}
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleDetail(item)}
                            title="Detail"
                            className="flex items-center justify-center h-8 w-8 rounded-xl bg-gray-100 dark:bg-white/5 text-custom-gelap dark:text-white transition-all hover:bg-gray-200 dark:hover:bg-white/10"
                          >
                            <MdVisibility size={15} />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleEdit(item)}
                            title="Edit"
                            className="flex items-center justify-center h-8 w-8 rounded-xl bg-custom-merah-terang/10 text-custom-merah-terang transition-all hover:bg-custom-merah-terang hover:text-white"
                          >
                            <MdEdit size={15} />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDelete(item)}
                            title="Delete"
                            className="flex items-center justify-center h-8 w-8 rounded-xl bg-red-50 text-red-500 dark:bg-red-500/10 transition-all hover:bg-red-500 hover:text-white"
                          >
                            <MdDelete size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* =====================================================
            PAGINATION
        ====================================================== */}

        {filter.stage === "CLOSED" && pagination.total > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white dark:bg-custom-gelap border border-gray-100 dark:border-white/5 rounded-2xl px-4 py-3">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-bold text-gray-400 uppercase whitespace-nowrap">
                  Tampilkan
                </span>

                <select
                  value={perPage}
                  onChange={(e) => handlePerPageChange(e.target.value)}
                  className="h-8 rounded-xl border border-gray-100 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-2 text-[10px] font-black text-custom-gelap dark:text-white outline-none focus:border-custom-merah-terang cursor-pointer"
                >
                  {PER_PAGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option} / halaman
                    </option>
                  ))}
                </select>
              </div>

              <div className="h-5 w-px bg-gray-100 dark:bg-white/10" />

              <p className="text-[9px] font-bold text-gray-400 uppercase whitespace-nowrap">
                Menampilkan {(pagination.page - 1) * pagination.per_page + 1}–
                {Math.min(
                  pagination.page * pagination.per_page,
                  pagination.total,
                )}{" "}
                dari {pagination.total} item
              </p>
            </div>

            {pagination.total_pages > 1 && (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((current) => current - 1)}
                  className="flex items-center justify-center h-8 w-8 rounded-xl bg-gray-100 dark:bg-white/5 text-custom-gelap dark:text-white transition-all hover:bg-gray-200 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Halaman sebelumnya"
                >
                  <span className="text-xs">‹</span>
                </button>

                {Array.from(
                  { length: pagination.total_pages },
                  (_, index) => index + 1,
                )
                  .filter(
                    (pageNumber) =>
                      pageNumber === 1 ||
                      pageNumber === pagination.total_pages ||
                      Math.abs(pageNumber - page) <= 1,
                  )
                  .map((pageNumber, index, pages) => {
                    const previousPage = pages[index - 1];

                    return (
                      <React.Fragment key={pageNumber}>
                        {previousPage && pageNumber - previousPage > 1 && (
                          <span className="flex items-center justify-center h-8 w-5 text-[10px] font-black text-gray-400">
                            ...
                          </span>
                        )}

                        <button
                          type="button"
                          disabled={loading}
                          onClick={() => setPage(pageNumber)}
                          className={`flex items-center justify-center h-8 min-w-8 px-2 rounded-xl text-[10px] font-black transition-all ${
                            page === pageNumber
                              ? "bg-custom-merah-terang text-white shadow-md shadow-custom-merah-terang/20"
                              : "bg-gray-100 dark:bg-white/5 text-custom-gelap dark:text-white hover:bg-gray-200 dark:hover:bg-white/10"
                          } disabled:cursor-not-allowed`}
                          aria-label={`Halaman ${pageNumber}`}
                          aria-current={
                            page === pageNumber ? "page" : undefined
                          }
                        >
                          {pageNumber}
                        </button>
                      </React.Fragment>
                    );
                  })}

                <button
                  type="button"
                  disabled={page >= pagination.total_pages || loading}
                  onClick={() => setPage((current) => current + 1)}
                  className="flex items-center justify-center h-8 w-8 rounded-xl bg-custom-gelap dark:bg-custom-cerah text-white transition-all hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Halaman berikutnya"
                >
                  <span className="text-xs">›</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Create */}
      {showCreate && (
        <ModalCreateQuotation
          show={showCreate}
          onClose={() => setShowCreate(false)}
          onSuccess={fetchQuotations}
        />
      )}

      {/* Modal Detail */}
      {showDetail && selectedQuotationId && (
        <ModalDetailQuotation
          show={showDetail}
          onClose={() => {
            setShowDetail(false);
            setSelectedQuotationId(null);
          }}
          quotationId={selectedQuotationId}
        />
      )}

      {/* Modal Edit */}
      {showEdit && selectedQuotationId && (
        <ModalEditQuotation
          show={showEdit}
          onClose={() => {
            setShowEdit(false);
            setSelectedQuotationId(null);
          }}
          quotationId={selectedQuotationId}
          onSuccess={fetchQuotations}
        />
      )}

      {/* Modal Preview Document */}
      {showDocumentPreview && selectedDocumentUrl && (
        <DocumentPreviewModal
          show={showDocumentPreview}
          url={selectedDocumentUrl}
          title={selectedDocumentTitle}
          onClose={() => {
            setShowDocumentPreview(false);
            setSelectedDocumentUrl("");
            setSelectedDocumentTitle("");
          }}
        />
      )}
    </>
  );
};

export default Quotation;
